import { ThemeHelperInterface } from '@accredible-frontend-v2/models';
import { AccredibleBaseThemeHelper } from '@accredible-frontend-v2/utils/base-theme-helper';
import { accredibleCustomThemesMetadata } from '@accredible-frontend-v2/utils/themes';
import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { SeoCourseParams, SeoParams, SpiderableOptions } from './seo.model';

@Injectable({
  providedIn: 'root',
})
export class AccredibleSeoService {
  private readonly _document = inject(DOCUMENT);
  private readonly _meta = inject(Meta);
  private readonly _title = inject(Title);

  private _theme: string;

  setTheme(ThemeHelper: ThemeHelperInterface): void {
    this._theme = ThemeHelper.getTheme();
  }

  generateTags(config?: SeoParams): void {
    const themeMetadata: SeoParams = accredibleCustomThemesMetadata[this._theme];

    // default values
    config = {
      title: themeMetadata.title,
      description: themeMetadata.description,
      identity: themeMetadata.identity,
      spiderable: themeMetadata.spiderable,
      image: themeMetadata.image,
      url: '',
      twitterCard: 'summary',
      twitterSite: themeMetadata.twitterSite,
      twitterCreator: themeMetadata.twitterCreator,
      twitterImage: themeMetadata.image,
      ...config,
    };

    if (themeMetadata.duplicateOf) {
      AccredibleBaseThemeHelper.setThemeFavicon(themeMetadata.duplicateOf);
    }

    // Content
    this._title.setTitle(config.title);
    this._meta.updateTag({
      name: 'author',
      content: config.identity,
    });
    this._meta.updateTag({
      name: 'description',
      content: config.description,
    });

    // Facebook & others
    this._meta.updateTag({ property: 'og:type', content: 'website' });
    this._meta.updateTag({ property: 'og:site_name', content: config.identity });
    this._meta.updateTag({ property: 'og:title', content: config.title });
    this._meta.updateTag({ property: 'og:description', content: config.description });
    this._meta.updateTag({ property: 'og:image', content: config.image || themeMetadata.image });
    this._meta.updateTag({
      property: 'og:url',
      content: `${this._document.location.origin}${config.url}`,
    });

    // Twitter
    this._meta.updateTag({ name: 'twitter:card', content: config.twitterCard });
    this._meta.updateTag({ name: 'twitter:site', content: config.twitterSite });
    this._meta.updateTag({ name: 'twitter:creator', content: config.twitterCreator });
    this._meta.updateTag({ name: 'twitter:title', content: config.title });
    this._meta.updateTag({ name: 'twitter:description', content: config.description });
    this._meta.updateTag({
      name: 'twitter:image',
      content: config.twitterImage || themeMetadata.image,
    });

    this._updateSpiderableTags(config.spiderable);

    // Tell headless-render-api and Netlify pre-rendering services that meta tags are ready https://headless-render-api.com/docs/triggering-render-completion
    setTimeout(() => {
      (<any>window).prerendercloudReady = true;
      (<any>window).prerenderReady = true;
    }, 100);
  }

  generateCourseSeoScript(course: SeoCourseParams): void {
    const script = this._document.createElement('script') as HTMLScriptElement;
    script.type = 'application/ld+json';
    script.innerHTML = `
      {
        "@context": "https://schema.org",
        "@type": "Course",
        "name": "${course.name}",
        "description": "${course.description}",
        "provider": {
          "@type": "Organization",
          "name": "${course.organization?.name}",
          "sameAs": "${course.organization?.url.split('?')[0]}"
        }
      }
    `;

    this._document.head.appendChild(script);
  }

  /**
   * Determines the spiderable option based on two levels of priority.
   *
   * - If higherPriority is false, indexing is always disallowed.
   * - If higherPriority is true, defer to lowerPriority:
   *    - If lowerPriority is true, allow indexing.
   *    - If lowerPriority is false, disallow indexing.
   *    - If lowerPriority is undefined, default to allowing indexing.
   */
  getSpiderableByPriority(higherPriority: boolean, lowerPriority?: boolean): SpiderableOptions {
    if (!higherPriority) {
      return SpiderableOptions.NOINDEX_NOFOLLOW;
    }
    // At this point, higherPriority is true
    if (lowerPriority === false) {
      return SpiderableOptions.NOINDEX_NOFOLLOW;
    }
    // If lowerPriority is true or undefined, allow indexing
    return SpiderableOptions.ALL;
  }

  private _updateSpiderableTags(configSpiderable: SpiderableOptions): void {
    // Indexing / Spiders
    this._meta.updateTag({
      name: 'googlebot',
      content: configSpiderable,
    });
    this._meta.updateTag({
      name: 'bingbot',
      content: configSpiderable,
    });
    this._meta.updateTag({
      name: 'robots',
      content: configSpiderable,
    });
  }
}
